/**
 * Lit 组件基类 — 自动注入 Tailwind 共享样式到 Shadow DOM
 *
 * 所有 CRM 页面组件应继承 BaseElement 而非直接继承 LitElement。
 * 继承后组件的 Shadow DOM 会自动获得 Tailwind CSS 编译后的样式表，
 * 使得组件内可以使用 Tailwind 工具类（如 flex、p-4、text-sm 等）。
 *
 * 实现原理：
 * - 重写 createRenderRoot()，在创建 Shadow DOM 后通过 adoptedStyleSheets 注入共享样式
 * - 共享样式表对象（sharedStyleSheet）在内存中只有一份，所有组件复用同一引用
 * - 使用 CSSStyleSheet.replaceSync() 一次性编译，避免重复解析
 *
 * 关联模块：
 * - ./shared-styles.ts — 提供 sharedStyleSheet（Tailwind 编译后的 CSSStyleSheet）
 */
import { LitElement } from 'lit';
import { sharedStyleSheet } from './shared-styles.js';

/**
 * CRM 组件基类
 *
 * 继承 LitElement，所有 CRM 业务组件的基类。
 * 提供 Tailwind CSS 样式的自动注入能力。
 *
 * 用法：
 * ```ts
 * @customElement('my-component')
 * class MyComponent extends BaseElement {
 *   render() { return html`<div class="p-4 text-sm">Hello</div>`; }
 * }
 * ```
 */
export class BaseElement extends LitElement {
  /**
   * 创建 Shadow DOM 渲染根并注入共享样式
   *
   * 重写 LitElement 的 createRenderRoot 方法：
   * 1. 调用 super.createRenderRoot() 创建默认的 Shadow DOM
   * 2. 将 sharedStyleSheet（Tailwind 编译样式）添加到 adoptedStyleSheets 列表头部
   * 3. 保留组件自身的 adoptedStyleSheets（如有），实现样式叠加
   *
   * @returns ShadowRoot — 带有共享 Tailwind 样式的 Shadow DOM 根节点
   */
  protected override createRenderRoot(): ShadowRoot {
    const root = super.createRenderRoot() as ShadowRoot;
    // 将共享样式置于 adoptedStyleSheets 列表首位，组件内定义的 styles 可覆盖之
    root.adoptedStyleSheets = [sharedStyleSheet, ...(root.adoptedStyleSheets || [])];
    return root;
  }
}
